<template>
  <b-dropdown
    variant="outline-primary"
    right
    no-caret
    :disabled="exporting"
    class="d-inline-block ml-1"
  >
    <template #button-content>
      <feather-icon
        v-if="exporting"
        icon="LoaderIcon"
        class="spin"
      />
      <feather-icon
        v-else
        icon="PrinterIcon"
      />
      {{ exporting ? 'Hazırlanıyor...' : 'Basım' }}
    </template>
    <b-dropdown-item
      v-for="opt in exportOptions"
      :key="opt.type"
      @click="doExport(opt.type)"
    >
      <feather-icon
        :icon="opt.icon"
        size="16"
        class="mr-50"
      />
      {{ opt.label }}
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
import { BDropdown, BDropdownItem } from 'bootstrap-vue'
import axiosIns from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const ENTITY_CONFIG = {
  customers: {
    dataEndpoint: '/data/customers/',
    dataKey: 'customers',
    normalize(record) {
      return { customer: record || {} }
    },
  },
  suppliers: {
    dataEndpoint: '/data/suppliers/',
    dataKey: 'suppliers',
    normalize(record) {
      return { supplier: record || {} }
    },
  },
  interviews: {
    dataEndpoint: '/data/interviews/',
    dataKey: 'interviews',
    normalize(record) {
      const r = record || {}
      return {
        interview: r,
        customer: r.customers || {},
        interview_subjects: r.interview_subjects || {},
        interview_statuses: r.interview_statuses || {},
        meets: r.meets || {},
      }
    },
  },
  offers: {
    dataEndpoint: '/data/offers/',
    dataKey: 'offers',
    normalize(record) {
      const r = record || {}
      return {
        offer: r,
        customer: r.customers || {},
        offer_statuses: r.offer_statuses || {},
      }
    },
  },
  contracts: {
    dataEndpoint: '/data/contracts/',
    dataKey: 'contracts',
    normalize(record) {
      const r = record || {}
      return {
        contract: r,
        customer: r.customers || {},
      }
    },
  },
  tasks: {
    dataEndpoint: '/data/tasks/',
    dataKey: 'tasks',
    normalize(record) {
      const r = record || {}
      return {
        task: r,
        task_statuses: r.task_statuses || {},
        task_priorities: r.task_priorities || {},
        customer: r.customers || {},
        assigned_to: r.assigned_to || {},
        created_by: r.created_by || {},
        task_lines: r.task_lines || [],
      }
    },
  },
  follows: {
    dataEndpoint: '/data/follows/',
    dataKey: 'follows',
    normalize(record) {
      const r = record || {}
      return {
        follow: r,
        customer: r.customers || {},
        supplier: r.suppliers || {},
        user: r.users || {},
        follow_lines: r.follow_lines || [],
      }
    },
  },
}

export default {
  name: 'PrintExportDropdown',
  components: {
    BDropdown,
    BDropdownItem,
  },
  props: {
    /** Modül: customers | suppliers | interviews | offers | contracts | tasks | follows */
    entityType: {
      type: String,
      required: true,
      validator: v => ['customers', 'suppliers', 'interviews', 'offers', 'contracts', 'tasks', 'follows'].includes(v),
    },
    /** Liste API'sine gönderilecek where koşulu (filtre parametreleri) */
    filterWhere: {
      type: Object,
      default: () => ({}),
    },
    /** Export için max kayıt (varsayılan 10000) */
    maxRecords: {
      type: Number,
      default: 10000,
    },
  },
  data() {
    return {
      exporting: false,
      exportOptions: [
        { type: 'excel', label: 'Excel indir', icon: 'FileTextIcon' },
        { type: 'pdf', label: 'PDF indir', icon: 'FileIcon' },
      ],
    }
  },
  methods: {
    showToast({
      title, text, variant = 'danger', icon = 'XIcon',
    }) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon, text, variant,
        },
      })
    },
    /** entity_type + type için varsayılan veya ilk şablonu getirir */
    async getTemplateForExport(printType) {
      const res = await this.$store.dispatch('printTemplates/getDataList', {
        where: {
          entity_type: this.entityType,
          type: printType,
          is_active: 1,
          deletedAt: null,
        },
        take: 20,
        skip: 0,
      })
      const list = (res && res.data) || this.$store.getters['printTemplates/dataList'] || []
      const defaultTpl = list.find(t => t.is_default)
      return defaultTpl || list[0] || null
    },
    async doExport(printType) {
      if (this.exporting) return
      const config = ENTITY_CONFIG[this.entityType]
      if (!config) {
        this.showToast({ title: 'Hata', text: 'Desteklenmeyen modül.' })
        return
      }

      this.exporting = true
      try {
        const template = await this.getTemplateForExport(printType)
        if (!template || !template.id) {
          this.showToast({
            title: 'Şablon bulunamadı',
            text: `Bu modül için tanımlı ${printType === 'excel' ? 'Excel' : 'PDF'} şablonu yok. Önce Basım Şablonlarından bir şablon tanımlayın.`,
            variant: 'warning',
            icon: 'AlertCircleIcon',
          })
          return
        }

        // Modül bazlı endpoint kullan (filtre verisi ile)
        const endpoint = printType === 'excel' ? '/print/module-excel' : '/print/module-pdf'

        // Filtre verisini hazırla
        const filterWhere = { ...this.filterWhere, deletedAt: null }

        // Modül özel seçenekler (örn: follows için)
        const options = {}
        if (this.entityType === 'follows') {
          const filterData = this.$store.getters['follows/filterData'] || {}
          if (filterData.is_active === 'expired') {
            options.expiredStatus = true
          }
          if (filterData.start_date) {
            options.startDate = filterData.start_date
          }
          if (filterData.end_date) {
            options.endDate = filterData.end_date
          }
          if (filterData.upcomingEnabled && filterData.upcomingDays) {
            options.upcomingDays = filterData.upcomingDays
          }
        }

        options.maxRecords = this.maxRecords

        const body = {
          templateId: template.id,
          entityType: this.entityType,
          filterWhere,
          options,
        }

        const res = await axiosIns.post(endpoint, body)
        const result = res.data && res.data.data
        const logId = result && result.logId
        const filename = (result && result.filename) || (printType === 'excel' ? 'export.xlsx' : 'export.pdf')

        if (!logId) {
          this.showToast({
            title: 'Basım hatası',
            text: res.data?.message || 'Dosya oluşturulamadı.',
          })
          return
        }

        await this.downloadFile(logId, filename, printType)
        this.showToast({
          title: 'Başarılı',
          text: `${printType === 'excel' ? 'Excel' : 'PDF'} dosyası indiriliyor.`,
          variant: 'success',
          icon: 'CheckIcon',
        })
      } catch (err) {
        console.error('PrintExportDropdown doExport:', err)
        this.showToast({
          title: 'Basım hatası',
          text: err.response?.data?.message || err.message || 'Dosya oluşturulurken bir hata oluştu.',
        })
      } finally {
        this.exporting = false
      }
    },
    async downloadFile(logId, filename) {
      const res = await axiosIns.get(`/print/download/${logId}`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
  },
}
</script>

<style scoped>
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
